import { Card, Row, Col, Table, Badge, ProgressBar, Spinner, Alert } from "react-bootstrap";
import { useLoyaltyBonus } from "./hooks/use-loyalty-bonus";
import { formatCurrency } from "../../utils";
import LoyaltySettlementsTable from "./components/LoyaltySettlementsTable";

const BonusLoyalty = () => {
    const {
        levelCalculations,
        activeCurrencies,
        startDate,
        endDate,
        progressPercentage,
        daysRemaining,
        isCurrentMonth,
        isLoading,
        error,
        refetch,
        loyaltyEnabled
    } = useLoyaltyBonus();

    // History data will be implemented in the future

    if (isLoading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Cargando...</span>
                </Spinner>
            </div>
        );
    }

    if (error) {
        return (
            <Alert variant="danger" className="mb-4">
                <Alert.Heading>Error al cargar datos</Alert.Heading>
                <p>{error.message}</p>
                <button
                    className="btn btn-outline-danger"
                    onClick={() => refetch()}
                >
                    Reintentar
                </button>
            </Alert>
        );
    }

    // Show activation prompt if loyalty is not enabled
    if (!loyaltyEnabled) {
        return (
            <div className="text-center py-5">
                <div className="mb-4">
                    <img
                        src="/images/shape-16.png"
                        className="mb-4"
                        alt="Activate loyalty"
                        width={200}
                        height={200}
                        style={{ opacity: 0.8 }}
                    />
                </div>

                <h3 className="fs-24 mb-3 text-success">
                    ¡Activa tus Regalías!
                </h3>

                <p className="mb-4 text-muted px-3">
                    Para comenzar a ganar regalías mensuales, necesitas realizar una compra
                    y activar tu red de referidos.
                </p>

                <div className="d-flex justify-content-center gap-3">
                    <a href="/store" className="btn btn-success py-2 px-4 fs-16 fw-medium">
                        <span className="d-inline-block py-1">Explorar Productos</span>
                    </a>
                    <a href="/team" className="btn btn-outline-success py-2 px-4 fs-16 fw-medium">
                        <span className="d-inline-block py-1">Mi Red</span>
                    </a>
                </div>

                <div className="mt-4 p-3 bg-success rounded">
                    <small className="text-white">
                        💰 <strong>Tip:</strong> Las regalías se calculan mensualmente
                        basadas en los puntos de lealtad de tu red por niveles.
                    </small>
                </div>
            </div>
        );
    }

    return (
        <>

            {/* Main Content Row */}
            <Row className="mb-4">
                {/* Left Side - Levels */}
                <Col lg={5}>
                    <Card className="border-0 rounded-3 bg-white p-4 h-100">
                        <h4 className="mb-4">Niveles y Puntos por Moneda (Mes Actual)</h4>
                        <div className="default-table-area">
                            <div className="table-responsive">
                                <Table>
                                    <thead>
                                        <tr>
                                            <th>Nivel</th>
                                            {activeCurrencies.map((curr) => (
                                                <th key={curr.currency}>
                                                    <div>{curr.currency}</div>
                                                    <small className="text-muted">Puntos | Regalía</small>
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {levelCalculations.map((level) => (
                                            <tr key={level.level} className={level.isLocked ? "table-secondary text-muted" : ""}>
                                                <td>
                                                    <Badge bg={level.isLocked ? "secondary" : "success"} className="me-2">
                                                        {level.level}
                                                    </Badge>
                                                    {level.name}
                                                    {level.isLocked && (
                                                        <small className="d-block text-muted">
                                                            <i className="fa fa-lock"></i>
                                                            Bloqueado
                                                        </small>
                                                    )}
                                                </td>
                                                {activeCurrencies.map((curr) => {
                                                    const currencyPoints = level.pointsByCurrency[curr.currency as keyof typeof level.pointsByCurrency] || 0;
                                                    const currencyValue = level.calculatedValueByCurrency[curr.currency as keyof typeof level.calculatedValueByCurrency] || 0;
                                                    return (
                                                        <td key={curr.currency} >
                                                            <div className="d-flex gap-2">
                                                                <p className={`mb-0 ${level.isLocked ? "text-muted" : ""}`}>
                                                                    {currencyPoints}
                                                                </p>
                                                                <p className={`mb-0 ${level.isLocked ? "text-muted" : "text-success"}`}>
                                                                    ({formatCurrency(currencyValue, curr.currency)})
                                                                </p>
                                                            </div>
                                                            {level.isLocked && (
                                                                <small className="text-muted">   Bloqueado</small>
                                                            )}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        ))}
                                        <tr className="table-active fw-bold">
                                            <td>Total</td>
                                            {activeCurrencies.map((curr) => (
                                                <td key={curr.currency}>
                                                    <div className="d-flex gap-2">
                                                        <p className="fw-bold mb-0">{curr.totalPoints}</p>
                                                        <p className="text-success fw-medium mb-0">
                                                            ({formatCurrency(curr.totalValue, curr.currency)})
                                                        </p>
                                                    </div>
                                                </td>
                                            ))}
                                        </tr>
                                    </tbody>
                                </Table>
                            </div>
                        </div>
                        <div className="mt-3">
                            <p className="mb-0 text-muted">
                                <i className="ri-information-line me-1"></i>
                                Las regalías se calculan como: Puntos × Porcentaje × Valor del Punto
                            </p>
                            <p className="mb-0 text-muted">
                                <i className="ri-information-line me-1"></i>
                                Para desbloquear niveles adicionales, necesitas tener al menos 3 miembros activos en tu red.
                            </p>
                        </div>
                    </Card>
                </Col>

                {/* Right Side - Stats */}
                <Col lg={7}>
                    {/* Royalties Status Cards - Stacked vertically */}
                    <Card className="border-0 rounded-3 bg-white p-4 mb-4">
                        <div className="d-flex align-items-center mb-3">
                            <div className="rounded-circle bg-success bg-opacity-10 p-3 me-3">
                                <i className="ri-money-dollar-circle-line text-success fs-24"></i>
                            </div>
                            <div>
                                <h5 className="mb-1">Estado de Regalías</h5>
                                <Badge bg={isCurrentMonth ? "success" : "danger"}>
                                    {isCurrentMonth ? "Activo" : "Inactivo"}
                                </Badge>
                            </div>
                        </div>
                        <p className="mb-2">
                            <strong>Período actual:</strong> {startDate} - {endDate}
                        </p>
                    </Card>

                    <Card className="border-0 rounded-3 bg-white p-4 mb-4">
                        <h5 className="mb-3">Progreso del Mes</h5>
                        <ProgressBar
                            now={progressPercentage}
                            style={{ height: '8px' }}
                            variant={progressPercentage >= 80 ? "success" : progressPercentage >= 50 ? "warning" : "info"}
                        />
                        <p className="mb-0 mt-2 text-muted">
                            {progressPercentage}% del período completado
                        </p>
                        <p className="mb-0">
                            ({daysRemaining} días restantes)
                        </p>
                    </Card>

                    <Card className="border-0 rounded-3 bg-white p-4 mb-4">
                        <h5 className="mb-3">Total para el período actual</h5>
                        {activeCurrencies.length > 0 ? (
                            <div className="mb-3">
                                {activeCurrencies.map((curr) => (
                                    <div key={curr.currency} className="d-flex align-items-center gap-2 mb-2">
                                        <span className="text-muted">{curr.currency}:</span>
                                        <h4 className="mb-0">{formatCurrency(curr.totalValue, curr.currency)}</h4>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <h2 className="mb-3 text-muted">Sin regalías acumuladas</h2>
                        )}
                        <p className="mb-0">
                            <i className="ri-information-line text-primary me-1"></i>
                            Las regalías se calculan mensualmente basadas en los puntos de cada nivel.
                        </p>
                    </Card>
                </Col>
            </Row>

            {/* Settlements History Table */}
            <div className="mb-4">
                <LoyaltySettlementsTable />
            </div>

        </>
    );
};

export default BonusLoyalty;
